/**
 * Single source of truth para identidade do site.
 * BAZÍLIO CONSÓRCIOS — fork SR-Investimentos (2026-07-01).
 *
 * Campos com "TODO:" precisam ser preenchidos pelo cliente antes do go-live.
 */

export type SiteBrand = {
  name: string;
  legalName: string;
  cnpj: string;
  address: {
    street: string;
    neighborhood: string;
    city: string;
    state: string;
    zip: string;
    country: "BR";
    geo?: { lat: number; lng: number };
  };
  phoneLandline: string;
  whatsapp: {
    number: string;
    displayNumber: string;
    defaultMessage: string;
  };
  email: string;
  hours: string;
  social: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
    youtube?: string;
  };
};

export type SiteTheme = {
  colors: Record<string, string>;
  fonts: { sans: string; display?: string };
  easings: { out: string; inOut: string };
};

export type SiteSeo = {
  defaultTitle: string;
  titleTemplate: string;
  defaultDescription: string;
  canonicalBase: string;
  ogImage: string;
  twitterHandle?: string;
};

export type SiteTracking = {
  gtmId: string;
  ga4Id: string;
  googleAdsId: string;
  googleAdsConversionLabels: { lead: string; whatsapp: string };
  metaPixelId: string;
  turnstileSiteKey: string;
};

export type SiteLegal = {
  privacyPolicyPath: string;
  termsPath: string;
  dpoEmail: string;
  bacenLink: string;
  consorcioLawLink: string;
  reclameAquiUrl?: string;
};

export type SiteConfig = {
  brand: SiteBrand;
  theme: SiteTheme;
  seo: SiteSeo;
  tracking: SiteTracking;
  legal: SiteLegal;
};

const env = (key: string, fallback = ""): string => {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  return v ?? fallback;
};

export const siteConfig: SiteConfig = {
  brand: {
    name: "Consórcio Bazilio",
    // Campos legais VAZIOS de propósito até o cliente fornecer dados reais.
    // Vazio => omitido do rodapé, das páginas legais e do JSON-LD (seo.ts omitEmpty).
    legalName: "Consórcio Bazilio", // TODO: razão social oficial (usa marca até lá)
    cnpj: "", // TODO: CNPJ oficial
    address: {
      street: "", // TODO: endereço oficial
      neighborhood: "",
      city: "",
      state: "",
      zip: "", // TODO
      country: "BR",
    },
    phoneLandline: "", // TODO: fixo real (vazio => oculto)
    whatsapp: {
      number: "5531987292967", // Mobile assumido do input `3187292967` — confirmar se fixo
      displayNumber: "(31) 98729-2967",
      defaultMessage:
        "Olá! Vim pelo site do Consórcio Bazilio e quero saber mais sobre consórcio.",
    },
    email: "", // TODO: email real (vazio => oculto)
    hours: "Seg a Sex: 8h às 18h · Sáb: 8h às 12h",
    social: {
      instagram: "https://instagram.com/camilabazilioconsorcio",
      facebook: "",
    },
  },
  theme: {
    colors: {
      // Paleta BAZÍLIO — preto + dourado + vermelho
      black: "#0a0a0a",
      blackDeep: "#000000",
      blackSoft: "#1c1c1c",
      gold: "#d4af37",
      goldDark: "#a8871f", // contraste WCAG AA quando usado como texto sobre light
      goldLight: "#f4c430",
      green: "#25D366", // WhatsApp mantido
      red: "#c8102e",
      redDark: "#a30d24",
      cream: "#f8f5ec",
    },
    fonts: {
      sans: '"Manrope", "Inter", system-ui, -apple-system, sans-serif',
      display: '"Playfair Display", "Manrope", Georgia, serif',
    },
    easings: {
      out: "cubic-bezier(0.23, 1, 0.32, 1)",
      inOut: "cubic-bezier(0.77, 0, 0.175, 1)",
    },
  },
  seo: {
    defaultTitle:
      "Consórcio Bazilio. Consórcio inteligente. Sem juros, sem entrada.",
    titleTemplate: "%s | Consórcio Bazilio",
    defaultDescription:
      "Consórcio de veículos, motos, imóveis, agro, pesados e serviços com atendimento consultivo da equipe Bazilio. Sem juros, sem entrada e com parcelas que cabem no bolso.",
    canonicalBase: env(
      "VITE_CANONICAL_BASE",
      "https://bazilio.hashtagcomunicacao.com.br"
    ),
    ogImage: "/og-bazilio.png",
    twitterHandle: "@bazilioconsorcios",
  },
  tracking: {
    gtmId: env("VITE_GTM_ID"),
    ga4Id: env("VITE_GA4_ID"),
    googleAdsId: env("VITE_GOOGLE_ADS_ID"),
    googleAdsConversionLabels: {
      lead: env("VITE_GOOGLE_ADS_LEAD_LABEL"),
      whatsapp: env("VITE_GOOGLE_ADS_WHATSAPP_LABEL"),
    },
    metaPixelId: env("VITE_META_PIXEL_ID"),
    turnstileSiteKey: env("VITE_TURNSTILE_SITE_KEY"),
  },
  legal: {
    privacyPolicyPath: "/politica-privacidade",
    termsPath: "/termos-de-uso",
    dpoEmail: "dpo@hashtagcomunicacao.com.br", // TODO: email DPO real
    bacenLink: "https://www.bcb.gov.br/estabilidadefinanceira/consorcio",
    consorcioLawLink:
      "https://www.planalto.gov.br/ccivil_03/_ato2007-2010/2008/lei/l11795.htm",
  },
};

export const whatsappHref = (message?: string): string => {
  const text = encodeURIComponent(
    message ?? siteConfig.brand.whatsapp.defaultMessage
  );
  return `https://wa.me/${siteConfig.brand.whatsapp.number}?text=${text}`;
};

export const telHref = (): string => `tel:+${siteConfig.brand.whatsapp.number}`;

export const landlineTelHref = (): string =>
  `tel:${siteConfig.brand.phoneLandline.replace(/\D/g, "")}`;
